import { zodResolver } from '@hookform/resolvers/zod';
import { useDependency } from 'ditox-react';
import { Add } from 'iconsax-react';
import { isEmpty, pick } from 'rambda';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useToggle } from 'usehooks-ts';
import { z } from 'zod';
import { MentionMenuContainer } from '@/components/conversations/mention-menu/mention-menu';
import { WEB_SOCKET_MANAGER_TOKEN } from '@/container';
import { ConversationFormProvider } from '@/hooks/conversations/use-conversation-form';
import useConversationStore, { useConversationHandlers } from '@/hooks/conversations/use-conversation-store';
import useGroupMembers from '@/hooks/groups/use-group-members';
import {
  ConversationMessageContentType,
  type ConversationMessageTypeUnion,
  conversationMessageUnionScheme,
} from '@/models/conversations/conversation';
import { uploadableScheme } from '@/models/uploadable';
import { useAuthStore } from '@/stores/auth';
import { useConversationSessionStore } from '@/stores/conversation-session';
import { addToast } from '@/stores/toast';
import { FILE_LIMIT_MAP, FileType } from '@/utils/file-type';
import cn from '@/utils/merge-class-names';
import { generateSessionKey } from '@/utils/session';
import { ConversationAttachmentForm } from '../attachment-form/conversation-attachment-form';
import ConversationRecordForm from '../conversation-record-form';
import ConversationFormButton from './conversation-form-button';
import ConversationFormQuoteArea from './conversation-form-quote-area';
import ConversationTextArea from './conversation-text-area';
import type { EditorActions } from './quill-editor';

export interface ConversationInputProps {
  layout?: 'full' | 'mini';
  isLoading?: boolean;
  disabled?: boolean;
}

export interface ConversationInputForwardedProps {
  quote: (quote: ConversationMessageTypeUnion) => void;
  mention: (mention: ConversationMessageTypeUnion) => void;
}

export const ConversationForm = forwardRef<ConversationInputForwardedProps, ConversationInputProps>(
  ({ layout = 'full', disabled, isLoading }, forwardedRef) => {
    const { t } = useTranslation();
    const userId = useAuthStore((s) => s.auth?.UserID);

    const [isAttachmentOpen, toggleAttachmentOpen, setAttachmentOpen] = useToggle(false);
    const editorRef = useRef<EditorActions>(null);
    const [isRecorderOpen, toggleRecorderOpen, setRecorderOpen] = useToggle(false);
    const { type: sessionType, id: sessionId } = useConversationStore();
    const { handleSubmit } = useConversationHandlers();
    const webSocketManager = useDependency(WEB_SOCKET_MANAGER_TOKEN);

    const { members } = useGroupMembers(sessionType === 'session' ? null : sessionId);
    const sessionKey = generateSessionKey(sessionType, sessionId);
    const { updateDraft, deleteDraft, hydrated } = useConversationSessionStore();

    const draft = useConversationSessionStore((s) => s.drafts[sessionKey]);

    const memoDraft = useMemo(() => (hydrated ? draft : undefined), [hydrated, draft]);

    const form = useForm<ConversationFormDataType>({
      reValidateMode: 'onSubmit',
      resolver: zodResolver(conversationFormSchema),
      defaultValues: {
        Content: '',
      },
    });

    function handleUpdateDraft() {
      const formValues = form.getValues();

      const draft = pick(['Content', 'Quote', 'AtIDs'], formValues);

      if (draft.Content || draft.Quote) {
        updateDraft(sessionKey, draft);
      } else {
        deleteDraft(sessionKey);
      }
    }

    function handleMentionMember(memberId: number) {
      editorRef.current?.insertMention(memberId);

      const atIds = memberId === -1 ? [0] : [memberId];

      form.setValue('AtIDs', atIds);
    }

    function handleResetForm() {
      form.reset();
      deleteDraft(sessionKey);
    }

    function handleToggleFileInput() {
      toggleAttachmentOpen();
      setRecorderOpen(false);
    }

    const membersWithoutSelf = Object.values(members).filter((m) => m.UserID !== userId);

    const mentionMemberList = useMemo(() => {
      const mentionAll = {
        UserID: -1,
        DisplayName: t('conversation.mention.all'),
        UserHeadImg: '',
        UserNickName: t('conversation.mention.all'),
      };

      return [mentionAll, ...membersWithoutSelf];
    }, [membersWithoutSelf, t]);

    async function submit(data: ConversationFormDataType) {
      const { Attachment, AttachmentType, Content, ...rest } = data;

      const contentDom = new DOMParser().parseFromString(Content ?? '', 'text/html');
      const stringContent = contentDom.body.textContent ?? '';
      const rawIds = Array.from(contentDom.body.querySelectorAll('span[data-at-id]'))
        .map((span) => Number.parseInt(span.getAttribute('data-at-id') ?? '0', 10))
        .filter((id) => !Number.isNaN(id));

      const atIds = rawIds.some((id) => id === -1)
        ? [0]
        : rawIds.filter((id) => membersWithoutSelf.find((m) => m.UserID === id));

      if (webSocketManager.state !== 'connected') {
        addToast({
          type: 'warning',
          message: t('connection.retrying'),
        });
        return Promise.reject();
      }

      if (!isEmpty(stringContent)) {
        await handleSubmit({
          ...rest,
          Content: stringContent ?? '',
          AtIDs: atIds,
        });
      }

      if (Attachment && !isEmpty(Attachment)) {
        await handleSubmit({
          ...rest,
          AtIDs: atIds,
          Attachment,
          AttachmentType,
        });
      }

      editorRef.current?.reset(!Attachment || isEmpty(Attachment));
      handleResetForm();
      setAttachmentOpen(false);
    }

    // NOTE: 修正 Android 選擇文件後 WebSocket 重新連線，並在尚未連線成功時提交導致失敗的問題，通過延遲 500ms 送出解決。
    const delayedSubmit = async (data: ConversationFormDataType) => {
      if (data.AttachmentType === FileType.File) {
        await webSocketManager.ensureConnected();
      }

      return submit(data);
    };

    useImperativeHandle(forwardedRef, () => ({
      quote: (quote) => {
        if (!disabled) {
          form.setValue('Quote', quote);
          handleUpdateDraft();
        }
      },
      mention: (mention) => {
        if (!disabled && 'GroupID' in mention) {
          handleMentionMember(mention.UserID);
        }
      },
    }));

    useEffect(() => {
      if (draft) {
        form.setValue('Content', draft.Content, { shouldDirty: true });
        form.setValue('AtIDs', draft.AtIDs);
        form.setValue('Quote', draft.Quote);
      }
    }, [draft, form.setValue]);

    return (
      <form
        onSubmit={form.handleSubmit(delayedSubmit)}
        className='flex flex-1 flex-col items-stretch h-full gap-2 desktop:relative desktop:bg-base-100 desktop:gap-0'>
        <ConversationFormProvider
          value={{
            form,
            canRecord: layout === 'full',
            submit: form.handleSubmit(delayedSubmit),
            handleUpdateDraft,
            isRecorderOpen,
            toggleRecorderOpen,
            mentionMemberList,
          }}>
          <ConversationFormQuoteArea />

          <div className='relative flex min-h-14 items-center gap-2 px-5  desktop:gap-6 desktop:pb-2 desktop:bg-base-100'>
            {layout === 'full' && (
              <button
                type='button'
                className={cn('relative btn btn-square btn-ghost btn-sm desktop:self-center', {
                  hidden: isRecorderOpen,
                })}
                disabled={disabled}
                onClick={handleToggleFileInput}>
                {isAttachmentOpen && <span className='absolute inset-0 z-10 hidden desktop:block' />}
                <Add
                  className={cn('size-6 transition-transform', {
                    'rotate-45': isAttachmentOpen,
                  })}
                />
              </button>
            )}

            {sessionType === 'group' && !isEmpty(mentionMemberList) && (
              <MentionMenuContainer onClose={() => editorRef.current?.closeMentionMenu()} />
            )}

            <div className='flex items-center gap-2 flex-1 desktop:min-h-0 desktop:gap-4 transition-colors desktop:textarea desktop:bg-neutral-0  desktop:border-2 desktop:border-secondary desktop:py-1.5 desktop:rounded-lg'>
              {isRecorderOpen ? (
                <ConversationRecordForm isLoading={isLoading} />
              ) : hydrated ? (
                <ConversationTextArea
                  key={sessionId}
                  ref={editorRef}
                  disabled={disabled}
                  isLoading={isLoading}
                  initialValue={memoDraft?.Content}
                />
              ) : null}

              <ConversationFormButton
                isLoading={isLoading}
                disabled={disabled}
              />
            </div>
          </div>

          {layout === 'full' && (
            <ConversationAttachmentForm
              open={isAttachmentOpen}
              isLoading={isLoading}
              disabled={disabled}
              onClose={() => setAttachmentOpen(false)}
            />
          )}
        </ConversationFormProvider>
      </form>
    );
  },
);

ConversationForm.displayName = 'ConversationForm';

const conversationFormSchema = z
  .union([
    z.object({
      Content: z.string().optional(),
      Quote: conversationMessageUnionScheme.optional(),
      Attachment: z.array(uploadableScheme).optional(),
      AttachmentType: z.nativeEnum(FileType),
      AtIDs: z.array(z.number()).optional(),
    }),
    z.object({
      Content: z.string().trim().min(1, 'errors.string.length|count=1'),
      Quote: conversationMessageUnionScheme.optional(),
      ContentType: z.nativeEnum(ConversationMessageContentType).default(ConversationMessageContentType.Text).optional(),
      Attachment: z.never().optional(),
      AttachmentType: z.null().optional(),
      AtIDs: z.array(z.number()).optional(),
    }),
  ])
  .refine(
    (data) => {
      if (data.Attachment && data.AttachmentType) {
        return data.Attachment.length <= FILE_LIMIT_MAP[data.AttachmentType as FileType];
      }
      return true;
    },
    { message: 'error.multi-file-over-limit', path: ['Attachment'] },
  );

export type ConversationFormDataType = z.infer<typeof conversationFormSchema>;
